import { TestRun, TestResult, QuestionTrendData, QuestionDataPoint, RunComparison } from '../types';
import { appConfig } from '../config/appConfig';

/**
 * Service for managing test results and test runs.
 * Currently uses mock data but designed for easy integration with cloud storage.
 */
class TestResultsService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = appConfig.api.baseUrl;
  }

  /**
   * Load all available test runs
   * Future implementation will fetch from cloud storage container
   */
  async loadTestRuns(): Promise<TestRun[]> {
    // TODO: Replace with actual API call to fetch test runs from cloud storage
    // const response = await fetch(`${this.apiBaseUrl}/testing/runs`);
    // if (!response.ok) throw new Error('Failed to fetch test runs');
    // return await response.json();

    // For now, return empty array to use mock data in component
    return [];
  }

  /**
   * Load a specific test run by filename
   * Future implementation will fetch from cloud storage
   */
  async loadTestRun(filename: string): Promise<TestRun> {
    try {
      // TODO: Replace with actual API call to fetch specific test run
      // const response = await fetch(`${this.apiBaseUrl}/testing/runs/${encodeURIComponent(filename)}`);
      // if (!response.ok) throw new Error(`Failed to fetch test run: ${filename}`);
      // return await response.json();

      throw new Error(`Test run service not implemented yet: ${filename}`);
    } catch (error) {
      console.error('Failed to load test run:', error);
      throw error;
    }
  }

  /**
   * Upload a new test run
   * Future implementation will upload to cloud storage
   */
  async uploadTestRun(file: File): Promise<TestRun> {
    try {
      // TODO: Implement file upload to cloud storage
      // const formData = new FormData();
      // formData.append('file', file);
      // const response = await fetch(`${this.apiBaseUrl}/testing/upload`, {
      //   method: 'POST',
      //   body: formData
      // });
      // if (!response.ok) throw new Error('Failed to upload test run');
      // return await response.json();

      throw new Error('Test run upload not implemented yet');
    } catch (error) {
      console.error('Failed to upload test run:', error);
      throw error;
    }
  }

  /**
   * Get list of available CSV files in test-runs directory
   */
  async getAvailableCsvFiles(): Promise<{filename: string, displayName: string, description: string}[]> {
    // Return the known CSV files with metadata
    return [
      {
        filename: 'policy_qa_extracted.csv',
        displayName: 'HR Policy QA Dataset',
        description: 'HR Policy questions and answers'
      },
      {
        filename: 'pwms_qa.csv',
        displayName: 'PWMS QA Dataset', 
        description: 'PWMS support questions and answers'
      }
    ];
  }

  /**
   * Load and parse a specific CSV file from test-runs directory
   */
  async loadCsvFile(filename: string): Promise<TestResult[]> {
    try {
      const url = `/test-runs/${filename}`;
      console.log(`🔗 Fetching URL: ${url}`);
      
      const response = await fetch(url, {
        cache: 'no-cache', // Disable caching
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
      }
      
      const csvContent = await response.text();
      
      // Check the actual content to verify what we received
      const firstLine = csvContent.split('\n')[1] || '';
      const firstQuestion = firstLine.split(',')[0] || 'No question found';
      
      console.log(`📂 Loaded ${filename}: ${csvContent.length} chars`);
      console.log(`🔍 First question in ${filename}: "${firstQuestion}"`);
      
      const results = this.parsePolicyQaCsv(csvContent);
      
      console.log(`✅ Parsed ${results.length} results from ${filename}`);
      if (results.length > 0) {
        console.log(`📋 Parsed first question: "${results[0].question.substring(0, 60)}..."`);
      }
      
      return results;
    } catch (error) {
      console.error(`Failed to load CSV file ${filename}:`, error);
      throw error;
    }
  }

  /**
   * Load and parse the PWMS QA CSV file (new format)
   */
  async loadPwmsQaCsv(): Promise<TestResult[]> {
    return this.loadCsvFile('pwms_qa.csv');
  }

  /**
   * Load and parse the actual policy_qa_extracted.csv file
   */
  async loadPolicyQaExtractedCsv(): Promise<TestResult[]> {
    return this.loadCsvFile('policy_qa_extracted.csv');
  }

  /**
   * Parse the PWMS QA CSV format (new format with source content)
   */
  parsePwmsQaCsv(csvContent: string): TestResult[] {
    const results: TestResult[] = [];
    
    // Split by lines but handle multi-line entries properly
    const lines = this.splitCsvIntoRecords(csvContent);
    
    if (lines.length < 2) return [];

    // Expected headers from the PWMS CSV
    const expectedHeaders = [
      'incident', 'question', 'ideal_answer', 'generated_answer', 'sources', 
      'query', 'completeness', 'correctness', 'conciseness', 'is_satisfactory',
      'ai_analysis', 'source_status', 'source_queries_used', 'source_analysis', 'sources_sufficient'
    ];

    // Parse header row
    const headerLine = lines[0];
    const headers = this.parseCsvLine(headerLine).map(h => h.trim().toLowerCase());
    
    console.log('PWMS CSV Headers found:', headers);
    console.log('Expected headers:', expectedHeaders);

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      try {
        const values = this.parseCsvLine(line);
        
        // Skip incomplete rows
        if (values.length < 10) continue;

        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] ? values[index].trim() : '';
        });

        // Extract source content and source names from the sources field
        const { sourceNames, sourceContent } = this.extractSourcesAndContent(row.sources || '');
        
        // Map CSV columns to TestResult interface
        const isSatisfactory = row.is_satisfactory === 'true' || row.is_satisfactory === 'True';
        
        const testResult: TestResult = {
          incident: row.incident || '',
          question: row.question || '',
          ideal_answer: row.ideal_answer || '',
          generated_answer: row.generated_answer || '',
          sources: sourceNames, // Use extracted source names for analysis
          source_content: sourceContent, // Full source content for expandable display
          query: row.query || '',
          completeness: parseInt(row.completeness) || 0,
          correctness: parseInt(row.correctness) || 0,
          conciseness: parseInt(row.conciseness) || 0,
          is_satisfactory: isSatisfactory,
          analysis: row.ai_analysis || row.analysis || '',
          source_status: this.mapSourceStatus(row.source_status, isSatisfactory),
          source_queries_used: row.source_queries_used || '',
          source_analysis: row.source_analysis || '',
          patch_notes: row.patch_notes || '',
          timestamp: row.timestamp || new Date().toISOString()
        };

        results.push(testResult);
      } catch (error) {
        console.warn(`Failed to parse PWMS CSV line ${i + 1}:`, error);
        continue;
      }
    }

    console.log(`Successfully parsed ${results.length} PWMS test results from CSV`);
    return results;
  }

  /**
   * Extract source names and content from the sources field
   */
  private extractSourcesAndContent(sourcesField: string): { sourceNames: string; sourceContent: string } {
    if (!sourcesField) {
      return { sourceNames: '', sourceContent: '' };
    }

    // Split by source references like "[1] filename" or "[2] filename"
    const sourcePattern = /\[(\d+)\]\s*([^,\n]+?)(?:,\s*Page\s*\d+)?:?\s*/g;
    const sources: string[] = [];
    let content = sourcesField;
    let match;

    // Extract source references
    while ((match = sourcePattern.exec(sourcesField)) !== null) {
      const sourceRef = match[0];
      const filename = match[2].trim();
      sources.push(filename);
      
      // Remove the source reference from content to leave just the content
      content = content.replace(sourceRef, '').trim();
    }

    // Clean up the content - remove leading newlines and extra spaces
    content = content.replace(/^\n+/, '').replace(/\n\n+/g, '\n\n').trim();

    return {
      sourceNames: sources.join(', '),
      sourceContent: content
    };
  }

  /**
   * Parse the specific policy_qa_extracted.csv format (new format with source_content column)
   */
  parsePolicyQaCsv(csvContent: string): TestResult[] {
    const results: TestResult[] = [];
    
    // Split by lines but handle multi-line entries properly
    const lines = this.splitCsvIntoRecords(csvContent);
    
    if (lines.length < 2) return [];



    // Parse header row
    const headerLine = lines[0];
    const headers = this.parseCsvLine(headerLine).map(h => h.trim().toLowerCase());
    
    // Parse CSV rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      try {
        const values = this.parseCsvLine(line);
        
        // Skip incomplete rows
        if (values.length < 10) continue;

        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] ? values[index].trim() : '';
        });

        // Map CSV columns to TestResult interface
        const isSatisfactory = row.is_satisfactory === 'true' || row.is_satisfactory === 'True';
        
        const testResult: TestResult = {
          question: row.question || '',
          ideal_answer: row.ideal_answer || '',
          generated_answer: row.generated_answer || '',
          sources: row.sources || '', // Source names for analysis
          source_content: row.source_content || '', // Full source content for expandable display
          query: row.query || '',
          completeness: parseInt(row.completeness) || 0,
          correctness: parseInt(row.correctness) || 0,
          conciseness: parseInt(row.conciseness) || 0,
          is_satisfactory: isSatisfactory,
          analysis: row.analysis || '',
          source_status: this.mapSourceStatus(row.source_status, isSatisfactory),
          source_queries_used: row.source_queries_used || '',
          source_analysis: row.source_analysis || '',
          patch_notes: row.patch_notes || '',
          timestamp: row.timestamp || new Date().toISOString()
        };

        results.push(testResult);
      } catch (error) {
        console.warn(`Failed to parse CSV line ${i + 1}:`, error);
        continue;
      }
    }

    console.log(`✅ Successfully parsed ${results.length} test results from CSV`);
    return results;
  }

  /**
   * Split CSV content into complete records, handling multi-line entries
   */
  private splitCsvIntoRecords(csvContent: string): string[] {
    const records: string[] = [];
    const lines = csvContent.split('\n');
    let currentRecord = '';
    let quoteCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Count quotes in this line
      const lineQuoteCount = (line.match(/"/g) || []).length;
      quoteCount += lineQuoteCount;
      
      // Add line to current record
      if (currentRecord) {
        currentRecord += '\n' + line;
      } else {
        currentRecord = line;
      }
      
      // If quote count is even, we have a complete record
      if (quoteCount % 2 === 0) {
        records.push(currentRecord);
        currentRecord = '';
        quoteCount = 0;
      }
    }
    
    // Add any remaining record
    if (currentRecord) {
      records.push(currentRecord);
    }

    return records;
  }

  /**
   * Map source status values from CSV to interface types
   */
  private mapSourceStatus(status: string, isSatisfactory: boolean): TestResult['source_status'] {
    const cleanStatus = status?.toLowerCase().trim();
    
    switch (cleanStatus) {
      case 'sufficient_context_retrieved':
        return 'sufficient_context_retrieved';
      case 'sufficient_context_found':
        return 'sufficient_context_found';
      case 'insufficient_knowledge_base':
        return 'insufficient_knowledge_base';
      case 'partial_context_available':
        return 'partial_context_available';
      default:
        // For empty or unknown values, determine based on is_satisfactory
        if (isSatisfactory) {
          // If satisfactory and no source status specified, assume sufficient context was retrieved
          return 'sufficient_context_retrieved';
        } else {
          // If unsatisfactory and no source status specified, assume knowledge base issue
          return 'insufficient_knowledge_base';
        }
    }
  }

  /**
   * Parse CSV file content into TestResult objects (generic version)
   */
  parseCsvToTestResults(csvContent: string): TestResult[] {
    // Use the policy QA specific parser for better handling
    return this.parsePolicyQaCsv(csvContent);
  }

  /**
   * Parse a single CSV line, handling quoted values with commas
   */
  private parseCsvLine(line: string): string[] {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  /**
   * Analyze test results using AI
   * Future implementation will call actual AI service
   */
  async analyzeResults(prompt: string, results: TestResult[]): Promise<string> {
    try {
      // TODO: Replace with actual AI service call
      // const response = await fetch(`${this.apiBaseUrl}/testing/analyze`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ prompt, results })
      // });
      // if (!response.ok) throw new Error('Analysis failed');
      // const data = await response.json();
      // return data.analysis;

      // For now, return a basic analysis
      const unsatisfactoryCount = results.filter(r => !r.is_satisfactory).length;
      const successRate = ((results.length - unsatisfactoryCount) / results.length * 100).toFixed(1);
      
      return `Analysis for ${results.length} test results (${successRate}% success rate):

🔍 **Key Findings:**
- ${unsatisfactoryCount} questions need improvement
- Common failure patterns identified in source retrieval
- Response quality varies across question types

📊 **Recommendations:**
1. Review knowledge base coverage for failed questions
2. Optimize search algorithms for better context retrieval
3. Enhance prompt engineering for consistency
4. Consider expanding training data for edge cases

🎯 **Action Items:**
- Prioritize high-frequency failure questions
- Update documentation based on common gaps
- Implement feedback loop for continuous improvement`;
    } catch (error) {
      console.error('Failed to analyze results:', error);
      throw error;
    }
  }

  /**
   * Compare multiple test runs
   */
  compareTestRuns(runs: TestRun[]): {
    trends: {
      success_rate: { current: number; previous: number; change: number };
      avg_completeness: { current: number; previous: number; change: number };
      avg_correctness: { current: number; previous: number; change: number };
    };
    common_failures: string[];
    improved_areas: string[];
  } {
    if (runs.length < 2) {
      throw new Error('Need at least 2 test runs to compare');
    }

    const latest = runs[0];
    const previous = runs[1];

    const latestSuccessRate = (latest.satisfactory_count / latest.total_questions) * 100;
    const previousSuccessRate = (previous.satisfactory_count / previous.total_questions) * 100;

    const latestAvgCompleteness = latest.results.reduce((sum: number, r: TestResult) => sum + r.completeness, 0) / latest.results.length;
    const previousAvgCompleteness = previous.results.reduce((sum: number, r: TestResult) => sum + r.completeness, 0) / previous.results.length;

    const latestAvgCorrectness = latest.results.reduce((sum: number, r: TestResult) => sum + r.correctness, 0) / latest.results.length;
    const previousAvgCorrectness = previous.results.reduce((sum: number, r: TestResult) => sum + r.correctness, 0) / previous.results.length;

    // Find common failure questions
    const latestFailures = new Set(latest.results.filter((r: TestResult) => !r.is_satisfactory).map((r: TestResult) => r.question));
    const previousFailures = new Set(previous.results.filter((r: TestResult) => !r.is_satisfactory).map((r: TestResult) => r.question));
    const commonFailures = Array.from(latestFailures).filter((q: string) => previousFailures.has(q));

    // Find improved questions
    const latestSuccess = new Set(latest.results.filter((r: TestResult) => r.is_satisfactory).map((r: TestResult) => r.question));
    const improvedAreas = Array.from(latestSuccess).filter((q: string) => previousFailures.has(q));

    return {
      trends: {
        success_rate: {
          current: latestSuccessRate,
          previous: previousSuccessRate,
          change: latestSuccessRate - previousSuccessRate
        },
        avg_completeness: {
          current: latestAvgCompleteness,
          previous: previousAvgCompleteness,
          change: latestAvgCompleteness - previousAvgCompleteness
        },
        avg_correctness: {
          current: latestAvgCorrectness,
          previous: previousAvgCorrectness,
          change: latestAvgCorrectness - previousAvgCorrectness
        }
      },
      common_failures: commonFailures,
      improved_areas: improvedAreas
    };
  }

  /**
   * Compare multiple test runs over time and track individual question performance
   */
  compareMultipleTestRuns(runs: TestRun[]): RunComparison {
    if (runs.length < 2) {
      throw new Error('Need at least 2 test runs to compare');
    }

    // Sort runs by timestamp (oldest first for trend analysis)
    const sortedRuns = runs.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Find overlapping questions across all runs
    const overlappingQuestions = this.findOverlappingQuestions(sortedRuns);

    // Generate trend data for each overlapping question
    const questionTrends = this.generateQuestionTrends(sortedRuns, overlappingQuestions);

    // Calculate overall trends
    const overallTrends = this.calculateOverallTrends(sortedRuns);

    // Categorize questions by performance trend
    const improvingQuestions = questionTrends
      .filter(q => q.trend === 'improving')
      .map(q => q.question);

    const decliningQuestions = questionTrends
      .filter(q => q.trend === 'declining')
      .map(q => q.question);

    const consistentFailures = questionTrends
      .filter(q => q.dataPoints.every(dp => !dp.is_satisfactory))
      .map(q => q.question);

    return {
      overlappingQuestions,
      questionTrends,
      overallTrends,
      improvingQuestions,
      decliningQuestions,
      consistentFailures
    };
  }

  /**
   * Find questions that appear in multiple test runs
   */
  private findOverlappingQuestions(runs: TestRun[]): string[] {
    const questionCounts = new Map<string, number>();

    runs.forEach(run => {
      const questionsInRun = new Set(run.results.map(r => r.question.trim().toLowerCase()));
      questionsInRun.forEach(question => {
        questionCounts.set(question, (questionCounts.get(question) || 0) + 1);
      });
    });

    // Return questions that appear in at least 2 runs
    return Array.from(questionCounts.entries())
      .filter(([_, count]) => count >= 2)
      .map(([question, _]) => question);
  }

  /**
   * Generate trend data for each question across multiple runs
   */
  private generateQuestionTrends(runs: TestRun[], overlappingQuestions: string[]): QuestionTrendData[] {
    return overlappingQuestions.map(questionKey => {
      const dataPoints: QuestionDataPoint[] = [];

      runs.forEach(run => {
        const result = run.results.find(r => r.question.trim().toLowerCase() === questionKey);
        if (result) {
          dataPoints.push({
            timestamp: run.timestamp,
            runId: run.id,
            filename: run.filename,
            completeness: result.completeness,
            correctness: result.correctness,
            conciseness: result.conciseness,
            is_satisfactory: result.is_satisfactory,
            source_status: result.source_status,
            generated_answer: result.generated_answer,
            analysis: result.analysis
          });
        }
      });

      // Calculate trend direction
      const trend = this.calculateTrend(dataPoints);
      const overallChange = this.calculateOverallChange(dataPoints);

      // Use the original question text from the most recent result
      const originalQuestion = dataPoints[dataPoints.length - 1]?.generated_answer ? 
        runs.find(r => r.id === dataPoints[dataPoints.length - 1].runId)?.results.find(r => r.question.trim().toLowerCase() === questionKey)?.question || questionKey :
        questionKey;

      return {
        question: originalQuestion,
        dataPoints: dataPoints.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        trend,
        overallChange
      };
    });
  }

  /**
   * Calculate trend direction for a question based on its data points
   */
  private calculateTrend(dataPoints: QuestionDataPoint[]): 'improving' | 'declining' | 'stable' | 'unknown' {
    if (dataPoints.length < 2) return 'unknown';

    // Calculate improvement based on satisfactory status and average scores
    const scores = dataPoints.map(dp => {
      const avgScore = (dp.completeness + dp.correctness + dp.conciseness) / 3;
      return dp.is_satisfactory ? avgScore + 100 : avgScore; // Boost satisfactory results
    });

    const firstHalf = scores.slice(0, Math.ceil(scores.length / 2));
    const secondHalf = scores.slice(Math.floor(scores.length / 2));

    const firstAvg = firstHalf.reduce((sum, score) => sum + score, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, score) => sum + score, 0) / secondHalf.length;

    const change = secondAvg - firstAvg;

    if (Math.abs(change) < 5) return 'stable';
    return change > 0 ? 'improving' : 'declining';
  }

  /**
   * Calculate overall change percentage for a question
   */
  private calculateOverallChange(dataPoints: QuestionDataPoint[]): number {
    if (dataPoints.length < 2) return 0;

    const first = dataPoints[0];
    const last = dataPoints[dataPoints.length - 1];

    const firstScore = (first.completeness + first.correctness + first.conciseness) / 3;
    const lastScore = (last.completeness + last.correctness + last.conciseness) / 3;

    return ((lastScore - firstScore) / firstScore) * 100;
  }

  /**
   * Calculate overall trends across all runs
   */
  private calculateOverallTrends(runs: TestRun[]) {
    const runLabels = runs.map(r => r.filename || r.id);
    
    const satisfactoryRates = runs.map(run => 
      (run.satisfactory_count / run.total_questions) * 100
    );

    const avgCompleteness = runs.map(run => 
      run.results.reduce((sum, r) => sum + r.completeness, 0) / run.results.length
    );

    const avgCorrectness = runs.map(run => 
      run.results.reduce((sum, r) => sum + r.correctness, 0) / run.results.length
    );

    const avgConciseness = runs.map(run => 
      run.results.reduce((sum, r) => sum + r.conciseness, 0) / run.results.length
    );

    return {
      satisfactoryRate: { runs: runLabels, values: satisfactoryRates },
      avgCompleteness: { runs: runLabels, values: avgCompleteness },
      avgCorrectness: { runs: runLabels, values: avgCorrectness },
      avgConciseness: { runs: runLabels, values: avgConciseness }
    };
  }
}

export const testResultsService = new TestResultsService();